import { Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { find, get, isEmpty, map, pickBy } from 'lodash';
import numeral from 'numeral';
import queryString from 'query-string';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonGroupFilter, { IListButtonItem } from 'src/components/common/ButtonGroupFilter';
import FlagCountry from 'src/components/common/FlagCountry';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import DatePickerWithDefaultIcon from 'src/components/DatePicker';
import TreemapChart from 'src/components/HighChart/Treemap';
import { TREEMAP_PALLET_COLOR } from 'src/components/HighChart/Treemap/config';
import LinkToCompanyDetail, { IForwardedParamsCompanyDetailLink } from 'src/components/LinkToCompanyDetail';
import CustomTable from 'src/components/Table';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import { useQueryListCompetitor, useQueryTreemapChartCompetitor } from 'src/services/queries/useQueryCompanyTradeData';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier } from 'src/utils/constants/enum';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import { convertToStartCase, getStringOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';

const CompetitorTreemap = ({ company }: { company: ICompanyDetail }) => {
  const [unitFilter, setUnitFilter] = useState<KeyFilterChartTradeDataUnitBy>(KeyFilterChartTradeDataUnitBy.bol);

  const [fromTime, setFromTime] = useState(dayjs().subtract(2, 'y').format(FORMAT_DATE_TIME.yyyy_mm_dd));
  const [toTime, setToTime] = useState((dayjs().format(FORMAT_DATE_TIME.yyyy_mm_dd) as string) || null);
  const [listQueryParams, setListQueryParams] = useState({
    limit: 10,
    offset: 0,
    sort_by: KeyFilterChartTradeDataUnitBy.bol,
    order: 'desc',
  });

  const theme = useTheme();

  const { data, isFetching } = useQueryTreemapChartCompetitor({
    id: company.id,
    query: {
      company_id: company.id,
      start_date: fromTime,
      end_date: toTime,
      sort_by: unitFilter,
      order: 'desc',
    },
    enabled: Boolean(company.id) && dayjs(fromTime).isValid() && dayjs(toTime).isValid(),
  });
  const listCompetitor = useQueryListCompetitor({
    id: company.id,
    query: {
      company_id: company.id,
      start_date: fromTime,
      end_date: toTime,
      ...listQueryParams,
    },
    enabled: Boolean(company.id) && dayjs(fromTime).isValid() && dayjs(toTime).isValid(),
  });

  const { t } = useTranslation();

  const treeMapData = useMemo(() => {
    if (!data.data || isFetching) return [];

    return map(data.data, (item: ICompanyAggregatedByBOL, index) => ({
      name: item.supplier_id,
      value: Math.cbrt(Number(get(item, `percentage_${unitFilter}`))),
      isVisibleInLegend: true,
      sortIndex: index,
    }));
  }, [data.data, unitFilter]);

  const configColumn: ConfigCell<ICompanyAggregatedByBOL>[] = [
    {
      id: 'company_name',
      width: 350,
      disableSort: true,
      label: 'Competitors',
      cell: (data: ICompanyAggregatedByBOL) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={getStringOrDefault(data.supplier_country_code)} />
            <LinkToCompanyDetail
              viewedAsType={TypeBuyerSupplier.supplier}
              id={data.supplier_id}
              companyName={data.supplier_name}
              params={{
                start_date: getStringOrDefault(fromTime),
                end_date: getStringOrDefault(toTime),
              }}
            />
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'percentage',
      label: 'Proportion (%)',
      disableSort: true,
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(data[`percentage_${unitFilter}` as keyof ICompanyAggregatedByBOL])}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(numeral(data.bol).format('O,O'))}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(numeral(data.weight).format('O,O'))}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      width: 150,
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderTextOrDefault(numeral(data.quantity).format('O,O'))}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value',
      label: t('trade_data.port_origin.table.value'),
      disableSort: true,
      width: 150,
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          --
        </Typography>
        // <Typography variant="body2" color="text.secondary">
        //   {renderTextOrDefault(numeral(data.value).format('O,O'))}
        // </Typography>
      ),
    },
  ];

  const renderTreemap = () => {
    if (!isFetching && isEmpty(data.data))
      return (
        <CompanyInfoNoData
          title={t('trade_data.common.notice_no_data.title')}
          content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
          sxContainer={{
            borderWidth: 0,
          }}
        />
      );

    return (
      <TreemapChart
        dataLabelFormatter={function () {
          const point: Highcharts.Point = this.point;
          const option = find(data.data, ({ supplier_id }) => supplier_id === point.options.name);
          const flag = getEmojiFlag(option?.supplier_country_code);

          const forwardedParams: IForwardedParamsCompanyDetailLink = pickBy(
            {
              start_date: fromTime,
              end_date: toTime,
              viewedAsType: 'supplier',
            },
            Boolean,
          );
          const query = '?' + queryString.stringify(forwardedParams);
          const url = option?.supplier_id
            ? AuthRouteKeys.CompanyDetail.replace(':id?', option?.supplier_id || '') + query
            : '';
          return `<div style="font-size: 14px; font-weight: 500; color: ${theme.palette.secondary.main}">
            <a style="font-size: 14px; font-weight: 500; color: ${theme.palette.secondary.main}" href="${url}">
              <p style="display: block">${flag}</p>
              <p style="display: block">${option?.supplier_name}</p>
            </a>
          </div>`;
        }}
        tooltipFormatter={function () {
          const point: Highcharts.Point = this.point;
          const option = find(data.data, ({ supplier_id }) => supplier_id === point.options.name);
          const flag = getEmojiFlag(option?.supplier_country_code);
          const percentage = get(option, `percentage_${unitFilter}`);
          const value = get(option, `${unitFilter}`);

          return `<div style="display: flex; flexDirection: column; gap: 8px; font-weight: 600;">
                <div style="font-size: 24px">
                  ${percentage}%
                </div>
                <div style="font-size: 14px"> ${flag} ${convertToStartCase(option?.supplier_name)}</div>
                <div style="font-size: 14px; color: #6B7280">${convertToStartCase(unitFilter)} <span style="color: #fff">${numeral(value).format('0,0')}</span></div>
              </div>`;
        }}
        colors={TREEMAP_PALLET_COLOR}
        height={620}
        loading={isFetching}
        data={treeMapData}
      />
    );
  };

  if (
    !isFetching &&
    !listCompetitor.isFetching &&
    isEmpty(getArrayDefault(data)) &&
    isEmpty(getArrayDefault(listCompetitor.data.data))
  ) {
    return (
      <CompanyInfoNoData
        title={t('trade_data.common.notice_no_data.title')}
        content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <WhiteBoxContent>
        <Stack mb={3} spacing={3} direction={'row'} alignItems={'center'}>
          <Typography flex={1} mb={3} variant="h5">
            Market Share Chart
          </Typography>

          <Stack maxWidth={300} direction={'row'} alignItems={'center'} gap={1}>
            <DatePickerWithDefaultIcon
              value={fromTime ? dayjs(fromTime) : null}
              disableFuture
              onChange={(value) => {
                setToTime(null);
                setFromTime(dayjs(value).format(FORMAT_DATE_TIME.yyyy_mm_dd));
              }}
              label={t('trade_data.search_form.input.from')}
              format={FORMAT_DATE_TIME.MONTH_YEAR}
              slotProps={{
                textField: {
                  size: 'small',
                  required: true,
                },
              }}
              views={['month', 'year']}
            />
            -
            <DatePickerWithDefaultIcon
              minDate={dayjs(fromTime)}
              maxDate={dayjs(fromTime).add(5, 'y')}
              disabled={!dayjs(fromTime).isValid()}
              value={toTime ? dayjs(toTime) : null}
              disableFuture
              format={FORMAT_DATE_TIME.MONTH_YEAR}
              onChange={(value) => setToTime(dayjs(value).format(FORMAT_DATE_TIME.yyyy_mm_dd))}
              label={t('trade_data.search_form.input.trade_to')}
              slotProps={{
                textField: {
                  size: 'small',
                  required: true,
                },
              }}
              views={['month', 'year']}
            />
          </Stack>

          <ButtonGroupFilter
            size="small"
            label="Unit By"
            buttons={[
              {
                label: 'No. of B/L',
                key: KeyFilterChartTradeDataUnitBy.bol,
              },
              {
                label: 'Weight',
                key: KeyFilterChartTradeDataUnitBy.weight,
              },
              {
                label: 'Quantity',
                key: KeyFilterChartTradeDataUnitBy.quantity,
              },
              {
                label: 'USD',
                key: KeyFilterChartTradeDataUnitBy.value_usd,
                disabled: true,
              },
            ]}
            active={unitFilter}
            onClick={(value: IListButtonItem['key']) => {
              setUnitFilter(value as KeyFilterChartTradeDataUnitBy);
            }}
          />
        </Stack>

        {renderTreemap()}
      </WhiteBoxContent>

      <WhiteBoxContent>
        <Typography mb={3} variant="h5">
          Market Share Data List
        </Typography>

        <CustomTable
          tableMaxHeight={600}
          handleSort={({ sortBy, sort }) => {
            setListQueryParams({
              ...listQueryParams,
              ...{
                sort_by: sortBy as KeyFilterChartTradeDataUnitBy,
                order: sort as string,
              },
            });
          }}
          totalRecords={listCompetitor.data.total_records}
          dataKey={'buyer_id'}
          configColumn={configColumn}
          data={listCompetitor.data.data}
          onChangePage={(e) => setListQueryParams({ ...listQueryParams, ...{ offset: e } })}
          onChangePageSize={(e) => setListQueryParams({ ...listQueryParams, ...{ offset: 0, limit: e } })}
          loading={listCompetitor.isFetching}
          pageSize={listQueryParams.limit}
          page={listQueryParams.offset}
        />
      </WhiteBoxContent>
    </Stack>
  );
};

export default CompetitorTreemap;
